import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'
import echarts from '../config'
import { cleanChartConfig } from '../utils'

/**
 * Replaces a gradient color with an echarts LinearGradient
 * @param subject - The itemStyle object that may contain a gradient
 * @returns {(*&{color: LinearGradient})|*|undefined|null} The itemStyle with gradient replaced or unchanged
 */
function replaceGradient (subject) {
  if (typeof subject === 'undefined') return undefined
  if (subject === null) return null

  if (subject?.color === 'gradient') {
    return {
      ...subject,
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, subject.gradientColorStops || [])
    }
  }

  return subject
}

function ComponentBar ({
  seriesId,
  data,
  name,
  colorBy,
  legendHoverLink,
  xAxisIndex,
  yAxisIndex,
  polarIndex,
  roundCap,
  realtimeSort,
  showBackground,
  backgroundStyle,
  label,
  labelLine,
  itemStyle,
  labelLayout,
  emphasis,
  blur,
  select,
  selectedMode,
  stack,
  stackStrategy,
  sampling,
  cursor,
  barWidth,
  barMaxWidth,
  barMinWidth,
  barMinHeight,
  barMinAngle,
  barGap,
  barCategoryGap,
  large,
  largeThreshold,
  progressive,
  progressiveThreshold,
  progressiveChunkMode,
  dimensions,
  encode,
  seriesLayoutBy,
  datasetIndex,
  dataGroupId,
  clip,
  markPoint,
  markLine,
  markArea,
  zlevel,
  z,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationDelayUpdate,
  animationEasingUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, cleanChartConfig({
      type: 'bar',
      id: seriesId,
      data,
      name,
      colorBy,
      legendHoverLink,
      xAxisIndex,
      yAxisIndex,
      polarIndex,
      roundCap,
      realtimeSort,
      showBackground,
      backgroundStyle,
      label,
      labelLine,
      itemStyle: replaceGradient(itemStyle),
      labelLayout,
      emphasis,
      blur,
      select,
      selectedMode,
      stack,
      stackStrategy,
      sampling,
      cursor,
      barWidth,
      barMaxWidth,
      barMinWidth,
      barMinHeight,
      barMinAngle,
      barGap,
      barCategoryGap,
      large,
      largeThreshold,
      progressive,
      progressiveThreshold,
      progressiveChunkMode,
      dimensions,
      encode,
      seriesLayoutBy,
      datasetIndex,
      dataGroupId,
      clip,
      markPoint,
      markLine,
      markArea,
      zlevel,
      z,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationDelayUpdate,
      animationEasingUpdate,
      universalTransition,
      tooltip
    }))
  }, [
    seriesId,
    data,
    name,
    colorBy,
    legendHoverLink,
    xAxisIndex,
    yAxisIndex,
    polarIndex,
    roundCap,
    realtimeSort,
    showBackground,
    backgroundStyle,
    label,
    labelLine,
    itemStyle,
    labelLayout,
    emphasis,
    blur,
    select,
    selectedMode,
    stack,
    stackStrategy,
    sampling,
    cursor,
    barWidth,
    barMaxWidth,
    barMinWidth,
    barMinHeight,
    barMinAngle,
    barGap,
    barCategoryGap,
    large,
    largeThreshold,
    progressive,
    progressiveThreshold,
    progressiveChunkMode,
    dimensions,
    encode,
    seriesLayoutBy,
    datasetIndex,
    dataGroupId,
    clip,
    markPoint,
    markLine,
    markArea,
    zlevel,
    z,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationDelayUpdate,
    animationEasingUpdate,
    universalTransition,
    tooltip,
    setSeries
  ])

  return null
}

ComponentBar.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.array,

  /** The policy to take color from option.color. Valid values:

   'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
   'data': assigns colors in the palette according to data items, with each data item using a different color. */
  colorBy: PropTypes.oneOf(['series', 'data']),

  /** Whether to enable highlighting chart when legend is being hovered. */
  legendHoverLink: PropTypes.bool,

  /** The coordinate used in the series, whose options are:
   'cartesian2d'
   Use a two-dimensional rectangular coordinate (also known as Cartesian coordinate), with xAxisIndex and yAxisIndex to assign the corresponding axis component.
   'polar'
   Use polar coordinates, with polarIndex to assign the corresponding polar coordinate component. */
  // coordinateSystem: PropTypes.oneOf(['cartesian2d', 'polar']),

  /** Index of x axis to combine with, which is useful for multiple x axes in one chart. */
  xAxisIndex: PropTypes.number,

  /** Index of y axis to combine with, which is useful for multiple y axes in one chart. */
  yAxisIndex: PropTypes.number,

  /** Index of polar coordinate to combine with, which is useful for multiple polar axes in one chart. */
  polarIndex: PropTypes.number,

  /** Whether to add round caps at the end of the bar sectors. Valid only for bar series on polar coordinates. */
  roundCap: PropTypes.bool,

  /** Whether to enable realtime sorting, which is used for bar-racing effect. Please refer to the tutorial Dynamic Sorting Bar Chart in the Handbook. */
  realtimeSort: PropTypes.bool,

  /** Whether to show background behind each bar. Use backgroundStyle to set background style. */
  showBackground: PropTypes.bool,

  /** Background style of each bar if showBackground is set to be true. */
  backgroundStyle: PropTypes.object,

  /** Text label of , to explain some data information about graphic item like value, name and so on. label is placed under itemStyle in ECharts 2.x. In ECharts 3, to make the configuration structure flatter, labelis taken to be at the same level with itemStyle, and has emphasis as itemStyle does. */
  label: PropTypes.object,

  /** Configuration of label guide line. */
  labelLine: PropTypes.object,

  /** The style of the symbol point of broken line. */
  itemStyle: PropTypes.object,

  /** Unified layout configuration of labels.
   It provide a chance to adjust the labels' (x, y) position, alignment based on the original layout each series provides.
   This option can be a callback with following parameters. */
  labelLayout: PropTypes.object,

  /** Highlight style of the graphic. */
  emphasis: PropTypes.object,

  /** Configurations of blur state. Available when emphasis.focus is set. */
  blur: PropTypes.object,

  /** Configurations of select state. Available when selectedMode is set. */
  select: PropTypes.object,

  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.
   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. */
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['single', 'multiple', 'series'])]),

  /** If stack the value. On the same category axis, the series with the same stack name would be put on top of each other.
   See also stackStrategy on how to customize how values are stacked.
   Notice: stack only supports stacking on value and log axis for now. time and category axis are not supported. */
  stack: PropTypes.string,

  /** How to stack values if the stack property has been set. Options:
   'samesign': only stack values if the value to be stacked has the same sign as the currently cumulated stacked value.
   'all': stack all values, irrespective of the signs of the current or cumulative stacked value.
   'positive': only stack positive values.
   'negative': only stack negative values. */
  stackStrategy: PropTypes.oneOf(['samesign', 'all', 'positive', 'negative']),

  /** The downsampling strategy used when the data size is much larger than pixel size. It will improve the performance when turned on. Defaults to be turned off, indicating that all the data points will be drawn. */
  sampling: PropTypes.oneOf(['lttb', 'average', 'min', 'max', 'minmax', 'sum']),

  /** The mouse style when mouse hovers on an element, the same as cursor property in CSS. */
  cursor: PropTypes.string,

  /** The width of the bar. Adaptive when not specified.
   Can be an absolute value like 40 or a percent value like '60%'. The percent is based on the calculated category width.
   In a single coordinate system, this attribute is shared by multiple 'bar' series. This attribute should be set on the last 'bar' series in the coordinate system, then it will be adopted by all 'bar' series in the coordinate system. */
  barWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The maximum width of the bar.
   Has higher priority than barWidth.
   Can be an absolute value like 40 or a percent value like '60%'. The percent is based on the calculated category width.
   In a single coordinate system, this attribute is shared by multiple 'bar' series. This attribute should be set on the last 'bar' series in the coordinate system, then it will be adopted by all 'bar' series in the coordinate system. */
  barMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The minimum width of the bar. In cartesian the default value is 1, otherwise the default value if null.
   Has higher priority than barWidth.
   Can be an absolute value like 40 or a percent value like '60%'. The percent is based on the calculated category width.
   In a single coordinate system, this attribute is shared by multiple 'bar' series. This attribute should be set on the last 'bar' series in the coordinate system, then it will be adopted by all 'bar' series in the coordinate system. */
  barMinWidth: PropTypes.number,

  /** The minimum width of bar. It could be used to avoid the following situation: the interaction would be affected when the value of some data item is too small. */
  barMinHeight: PropTypes.number,

  /** The minimum angle of bar. It could be used to avoid the following situation: the interaction would be affected when the value of some data item is too small. Valid only for bar series on polar coordinates */
  barMinAngle: PropTypes.number,

  /** The gap between bars between different series, is a percent value like '20%', which means 20% of the bar width.
   Set barGap as '-100%' can overlap bars that belong to different series, which is useful when making a series of bar be background.
   In a single coordinate system, this attribute is shared by multiple 'bar' series. This attribute should be set on the last 'bar' series in the coordinate system, then it will be adopted by all 'bar' series in the coordinate system. */
  barGap: PropTypes.string,

  /** The bar gap of a single series, by default, a suitable spacing is calculated based on the number of series in the bar chart. When there are more series, the spacing will be appropriately reduced, can be set as a fixed value.
   In a single coordinate system, this attribute is shared by multiple 'bar' series. This attribute should be set on the last 'bar' series in the coordinate system, then it will be adopted by all 'bar' series in the coordinate system. */
  barCategoryGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Whether to enable the optimization of large-scale data. It could be set when large data causes performance problem.
   After being enabled, largeThreshold can be used to indicate the minimum number for turning on the optimization.
   But when the optimization enabled, the style of single data item can't be customized any more. */
  large: PropTypes.bool,

  /** The threshold enabling the drawing optimization. */
  largeThreshold: PropTypes.number,

  /** progressive specifies the amount of graphic elements that can be rendered within a frame (about 16ms) if "progressive rendering" enabled.
   When data amount is from thousand to more than 10 million, it will take too long time to render all of the graphic elements. Since ECharts 4, "progressive rendering" is supported in its workflow, which processes and renders data chunk by chunk alone with each frame, avoiding to block the UI thread of the browser.
   Set progressive: 0 to disable progressive permanently. By default, progressive is auto-enabled when data amount is bigger than progressiveThreshold. */
  progressive: PropTypes.number,

  /** If current data amount is over the threshold, "progressive rendering" is enabled. */
  progressiveThreshold: PropTypes.number,

  /** Chunk approach, optional values:
   'sequential': slice data by data index.
   'mod': slice data by mod, which make the data items of each chunk coming from all over the data, bringing better visual effect while progressive rendering. */
  progressiveChunkMode: PropTypes.oneOf(['sequential', 'mod']),

  /** dimensions can be used to define dimension info for series.data or dataset.source. */
  dimensions: PropTypes.array,

  /** Define what is encoded to for each dimension of data */
  encode: PropTypes.object,

  /** When dataset is used, seriesLayoutBy specifies whether the column or the row of dataset is mapped to the series, namely, the series is "layout" on columns or rows. Optional values:
   'column': by default, the columns of dataset are mapped the series. In this case, each column represents a dimension.
   'row'：the rows of dataset are mapped to the series. In this case, each row represents a dimension. */
  seriesLayoutBy: PropTypes.oneOf(['column', 'row']),

  /** If series.data is not specified, and dataset exists, the series will use dataset. datasetIndex specifies which dataset will be used. */
  datasetIndex: PropTypes.number,

  /** A group ID assigned to all data items in the series.
   This option has a lower priority than groupId, which means when groupId is specified for a certain data item the
   dataGroupId will be simply ignored for that data item. For more information, please see series.data.groupId. */
  dataGroupId: PropTypes.string,

  /** If clip the overflow on the coordinate system. Clip results varies between series */
  clip: PropTypes.bool,

  /** Mark point in a chart. */
  markPoint: PropTypes.object,

  /** Use a line in the chart to illustrate. */
  markLine: PropTypes.object,

  /** Used to mark an area in chart. For example, mark a time interval. */
  markArea: PropTypes.object,

  /** zlevel value of all graphical elements in Line.
   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in
   different Canvases, which is a common optimization technique. We can put those frequently changed elements
   (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost,
   and should be used carefully on mobile phones to avoid crash.
   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in Line, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.
   z has a lower priority to zlevel, and will not create new Canvas. */
  z: PropTypes.number,

  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. */
  silent: PropTypes.bool,

  /** Whether to enable animation. */
  animation: PropTypes.bool,

  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. */
  animationThreshold: PropTypes.number,

  /** Duration of the first animation, which supports callback function for different data to have different animation effect: */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. */
  animationDelay: PropTypes.number,

  /** Time for animation to complete, which supports callback function for different data to have different animation effect: */
  animationDurationUpdate: PropTypes.number,

  /** Easing method used for animation. */
  animationEasingUpdate: PropTypes.number,

  /** Delay before updating animation, which supports callback function for different data to have different animation effects. */
  animationDelayUpdate: PropTypes.number,

  universalTransition: PropTypes.object,

  tooltip: PropTypes.object
}

ComponentBar.defaultProps = {
}

export default ComponentBar
